<template>
  <dl class="product-spec__item table-tr" key="index">
    <dd class="table-td" v-if="specList.length === 0">Quantity:</dd>
    <dd v-else class="table-td spec-color">
      <div class="table-tr" v-for="(color, idx) in data.productSpec" :key="idx">
        <label class="table-td">{{ color.name }}:</label>
        <span class="table-td">{{ color.value?.value }}</span>
      </div>
      <div class="price-spec-img" v-if="isMultiSize && data.previewImage" @mouseenter="onColorMouseEnter"
        @mouseleave="onColorMouseLeave">
        <ap-image :src="data.previewImage" width="30" height="30"> </ap-image>
      </div>
    </dd>
    <dd class="table-td spec-img" v-if="!isMultiSize && data.previewImage" @mouseenter="onColorMouseEnter"
      @mouseleave="onColorMouseLeave">
      <ap-image :src="data.previewImage" width="30" height="30"> </ap-image>
    </dd>
    <dd class="table-td td-spec-qty">
      <div class="multi_price" v-if="orderType !== OrderType.SpecSample && Array.isArray(data.quantity)
        ">
        <li v-for="sz in data.quantity" :key="sz.size">
          <div class="size_table_head">{{ sz.size }}</div>
          <div class="size_table_body">
            <input type="text" maxlength="6" class="qty-input" v-model="sz.quantity"
              @input="onQtyInput(sz.size, $event)" />
          </div>
          <div class="size_table_extra">
            <span v-if="sz.price > 0">+${{ sz.price.toFixed(2) }}</span>
          </div>
        </li>
      </div>
      <div v-else-if="Array.isArray(data.quantity)">
        <span>1</span>
        <select v-model="specSampleSizeType">
          <option v-for="(qty, sindex) in data.quantity" :key="sindex" :value="sindex">
            {{ qty.size }}
          </option>
        </select>
      </div>
      <div v-else>
        <input type="text" maxlength="6" class="qty-input" @input="onQtyInput('', $event)"
          :disabled="orderType === OrderType.SpecSample" :value="data.quantity.quantity" />
      </div>
    </dd>
    <dd class="table-td spec-stock" v-show="showStock">
      <div v-if="Array.isArray(data.quantity)">
        <div>
          <div v-for="(value, key) in stockHtml" :key="key">
            <dl class="stock-info">
              <dt v-if="key === 'inStock'" class="in-stock">In Stock:</dt>
              <dt v-else-if="key === 'lowStock' || key === 'insufficientStock'" class="low-stock">Low Stock:</dt>
              <dt v-else-if="key === 'outofStock'" class="out-of-stock">
                Out of Stock:
              </dt>
              <dt v-else-if="key === 'callforStock'" class="call-for-stock">
                Call for Stock:
              </dt>
              <dd class="stock-size" v-for="(val, i) in value" :key="i">
                {{ val }}
              </dd>
            </dl>
          </div>
        </div>
      </div>
      <div class="stock-info" v-else-if="data.quantity.stock">
        <div class="in-stock" v-show="data.quantity.stock.status === 'inStock'">
          In Stock
        </div>
        <div class="low-stock" v-show="data.quantity.stock.status === 'lowStock' || data.quantity.stock.status === 'insufficientStock'">
          Low Stock
        </div>
        <div class="out-of-stock" v-show="data.quantity.stock.status === 'outofStock'">
          <span v-show="data.quantity.stock.eta">
            Stock ETA
            {{
              data.quantity.stock.eta ? formatDate(data.quantity.stock.eta) : ''
            }}
            <img src="https://img66.anypromo.com/frontimg/2020/icon/question-1.svg" alt="question"
              @click="onOutStockClick(data.quantity.stock.eta)" /></span>
          <span v-show="!data.quantity.stock.eta">Out of Stock</span>
        </div>
        <div class="call-for-stock" v-show="data.quantity.stock.status === 'callforStock'">
          Call for Stock
          <img src="https://img66.anypromo.com/frontimg/2020/icon/question-1.svg" :title="PhoneNumber3" alt="question" />
        </div>
      </div>
    </dd>
    <dd class="table-td spec-btn" v-show="maxAssortNum > 1">
      <ap-button type="default" class="btn-remove" text @click="onRemoveItem">Delete</ap-button>
    </dd>
  </dl>
</template>

<script lang="ts">
import { defineComponent, type PropType, computed, inject } from 'vue'
import { ProductSpecModel } from '../../model'
import { OrderType, type ProductSpec } from '../../types'
import { ApMessageBox, ApButton, ApImage } from '@anypromo/components-pc'
import { formatDate, onlyNumber } from '@anypromo/shared/utils/helper'
import { SHOW_BIG_IMAGE_KEY } from '../../token'

export default defineComponent({
  props: {
    specList: {
      type: Array as PropType<ProductSpec[]>,
      default: () => []
    },
    orderType: {
      type: Number as PropType<OrderType>,
      default: OrderType.Normal
    },
    index: {
      type: Number,
      default: 0
    },
    data: {
      type: Object as PropType<ProductSpecModel>,
      default: {} as unknown
    },
    maxAssortNum: {
      type: Number,
      default: 1
    },
    showStock: {
      type: Boolean,
      default: true
    },
    isMultiSize: {
      type: Boolean,
      default: false
    }
  },
  components: { ApButton, ApImage },
  emits: {
    'update:specSampleSizeType': (index: number, value: string) => true,
    qtyChanged: (index: number, size: string, value: string) => true,
    remove: (model: ProductSpecModel) => true
  },
  setup (props, { emit }) {
    // const quantity = computed(() => props.data.quantity);

    const showBigImage = inject(SHOW_BIG_IMAGE_KEY)
    if (!showBigImage) {
      console.warn('build error')
    }

    const onQtyInput = (size: string, event: Event) => {
      const target = event.target as HTMLInputElement
      const oldValue = target.value
      const value = onlyNumber(oldValue)
      if (oldValue !== value) {
        target.value = value
      }
      // 更新model里的值
      emit('qtyChanged', props.index, size, value)
    }

    const specSampleSizeType = computed({
      get: () => props.data.specSampleSizeType,
      set: (val) => emit('update:specSampleSizeType', props.index, val)
    })

    const stockHtml = computed((): Record<string, string[]> => {
      const html: Record<string, string[]> = {}
      if (Array.isArray(props.data.quantity)) {
        props.data.quantity.forEach((f) => {
          if (f.stock) {
            if (f.stock.status) {
              if (html[f.stock.status]) {
                html[f.stock.status].push(f.size)
              } else {
                html[f.stock.status] = [f.size]
              }
            }
          }
        })
        // return html.join('')
      }
      return html
    })

    const onColorMouseEnter = (evt: MouseEvent) => {
      const target = evt.target as HTMLElement
      const imgUrl = props.data.previewImage
      const str = props.data.productSpec
        .map(function (item) {
          return (
            '<div><label>' +
            item.name +
            ':&nbsp;</label>' +
            item.value.value +
            '</div>'
          )
        })
        .join('')

      if (imgUrl) {
        if (showBigImage) {
          showBigImage.show(imgUrl, str, target)
        }
      }
    }

    const onOutStockClick = (eta: string | undefined) => {
      const message = `<div style="font-size:15px;width:600px">This color is currently out of stock with more arriving on ${eta}.<br />However, you can still place your order and we will provide an updated ship date once stock is in. </div>`
      ApMessageBox.alert(message, 'More stock coming soon!',{
        width:'auto',
        showClose:true
      })
    }
    const onColorMouseLeave = () => {
      // window.$('#zoomBigImage').remove()

      if (showBigImage) {
        showBigImage.hide()
      }
    }
    const onRemoveItem = () => {
      emit('remove', props.data)
    }
    return {
      OrderType,
      formatDate,
      onQtyInput,
      stockHtml,
      specSampleSizeType,
      onColorMouseEnter,
      onOutStockClick,
      onColorMouseLeave,
      onRemoveItem
    }
  },
  data() {
    return {
      PhoneNumber1: '',
      PhoneNumber2: '',       
      PhoneNumber3: '',  
    }
  },
  created() {
    this.PhoneNumber1 = (window as any).PhoneNumber1
    this.PhoneNumber2 = (window as any).PhoneNumber2
    this.PhoneNumber3 = (window as any).PhoneNumber3
  }
})
</script>

<style lang="less">
  .product-spec {
    &__list {
      max-width: 470px;
      min-width: 200px;
    }

    &__item {
      font-size: 13px;
      list-style: none;
      margin: 0;
      padding: 0;

      dd {
        font-size: 13px;
      }

      .spec-img {
        width: 50px;
      }

      .spec-color {

        span,
        label {
          padding: 0;
        }

        label {
          color: #999;
          margin: 0;
          font-weight: 500;
        }

        span {
          padding-left: 5px;
        }
      }

      .spec-stock {
        min-width: 95px;
      }

      .qty-input {
        width: 50px;
        height: 28px;
        border: 1px solid #ccc;
        text-align: center;

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          margin: 0;
          -webkit-appearance: none;
        }
      }

      .btn-remove {
        border: transparent;
        background: transparent;
        color: #4596ca;
        cursor: pointer;
        vertical-align: middle;
        width: auto;

        &:hover {
          color: #23527c;
          text-decoration: underline;
        }
      }
    }
  }

  .multi_price {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      display: inline-block;
      min-width: 46px;
      padding: 5px;
      vertical-align: top;
      color: #4e4e4e;

      .size_table_head {
        font-size: 13px;
        color: #999;
        text-align: center;
        height: 20px;
      }

      .size_table_body {
        text-align: center;
      }

      .size_table_extra {
        font-size: 12px;
        color: #4e4e4e;
        height: 10px;
        text-align: center;
      }
    }
  }

  .td-spec-qty {
    max-width: 240px;
  }

  .stock-info {
    margin-bottom: 0;

    dt {
      font-weight: 500;
    }

    .in-stock,
    .low-stock,
    .insufficient-stock,
    .out-of-stock,
    .call-for-stock {
      font-size: 11px;
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
      display: block;
      padding-top: 5px;
    }

    .in-stock {
      color: green;
    }

    .low-stock {
      color: #ff9900;
    }

    .insufficient-stock {
      color: #ff9900;
    }

    .out-of-stock {
      color: red;

      img {
        cursor: pointer;
        vertical-align: middle;
        padding-bottom: 2px;
        padding-left: 2px;
        width: 16px;
        height: 16px;
      }
    }

    .call-for-stock {
      color: #868686;

      img {
        vertical-align: middle;
        padding-bottom: 2px;
        padding-left: 2px;
        width: 16px;
        height: 16px;
      }
    }

    .stock-size {
      display: block;
      font-size: 11px;
    }
  }

  .price-spec-img {
    text-align: center;
    margin-top: 10px;

    img {
      width: 30px;
      height: 30px;
    }
  }
</style>
